/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CreateExportTask } from '../models/CreateExportTask';
import type { CreateImportTask } from '../models/CreateImportTask';
import type { ExportPreferences } from '../models/ExportPreferences';
import type { ExportRequest } from '../models/ExportRequest';
import type { ExportTask } from '../models/ExportTask';
import type { ImportRequest } from '../models/ImportRequest';
import type { ImportTask } from '../models/ImportTask';
import type { PaginatedExportTaskList } from '../models/PaginatedExportTaskList';
import type { PaginatedImportTaskList } from '../models/PaginatedImportTaskList';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class ImpexService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns PaginatedExportTaskList
     * @throws ApiError
     */
    public impexExportList({
        company,
        createdAtAfter,
        createdAtBefore,
        limit,
        offset,
    }: {
        company: string,
        createdAtAfter?: string,
        createdAtBefore?: string,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedExportTaskList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/impex/export/',
            query: {
                'company': company,
                'created_at_after': createdAtAfter,
                'created_at_before': createdAtBefore,
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns CreateExportTask
     * @throws ApiError
     */
    public impexExportCreate({
        requestBody,
    }: {
        requestBody?: ExportRequest,
    }): CancelablePromise<CreateExportTask> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/impex/export/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns ExportTask
     * @throws ApiError
     */
    public impexExportRetrieve({
        id,
    }: {
        /**
         * A UUID string identifying this export task.
         */
        id: string,
    }): CancelablePromise<ExportTask> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/impex/export/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns void
     * @throws ApiError
     */
    public impexExportDestroy({
        id,
    }: {
        /**
         * A UUID string identifying this export task.
         */
        id: string,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/impex/export/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Get the format and filter preferences from the user's most recent export of the given type.
     * @returns ExportPreferences
     * @throws ApiError
     */
    public impexExportLatestPreferencesRetrieve({
        company,
        exportType,
    }: {
        company: string,
        exportType: string,
    }): CancelablePromise<ExportPreferences> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/impex/export/latest-preferences/',
            query: {
                'company': company,
                'export_type': exportType,
            },
            errors: {
                404: `No response body`,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns PaginatedImportTaskList
     * @throws ApiError
     */
    public impexImportList({
        company,
        createdAtAfter,
        createdAtBefore,
        limit,
        offset,
    }: {
        company: string,
        createdAtAfter?: string,
        createdAtBefore?: string,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedImportTaskList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/impex/import/',
            query: {
                'company': company,
                'created_at_after': createdAtAfter,
                'created_at_before': createdAtBefore,
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns CreateImportTask
     * @throws ApiError
     */
    public impexImportCreate({
        requestBody,
    }: {
        requestBody?: ImportRequest,
    }): CancelablePromise<CreateImportTask> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/impex/import/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns ImportTask
     * @throws ApiError
     */
    public impexImportRetrieve({
        id,
    }: {
        /**
         * A UUID string identifying this import task.
         */
        id: string,
    }): CancelablePromise<ImportTask> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/impex/import/{id}/',
            path: {
                'id': id,
            },
        });
    }

}
